import { forwardRef, RefObject, useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Table } from "simplygrow-ui"

import { GroupService, TableService } from "../../services"
import { GroupMemberPpe, GroupMemberPpeProps } from "../../components/Groups/GroupMemberPpe"
import { ColorPalette } from "../../config"
import { UserFeedback } from "../../components/GeneralUI/Feedback/UserFeedback"
import { PeopleFlowCombinedReducer } from "../../store/reducers"
import { AssociationSettingsRepository } from "../../repositories"
import { PpeForProfile } from "../../types"
import { PpeInstanceRecordSchema } from "../../models/ppe"

import rowStyles from "./groupMembers.module.css"
import { TABLE_ROW_HEIGHT } from "../../constants/table"

const COLUMN_CONFIG = [
  { key: "idPassport", label: "ID / Passport", sizeFactor: 1 },
  { key: "name", label: "Name", sizeFactor: 1 },
  { key: "surname", label: "Surname", sizeFactor: 1 },
  { key: "jobType", label: "Job type", sizeFactor: 1 },
  { key: "startDate", label: "Scheduled start", sizeFactor: 1 },
  { key: "terminationDate", label: "Scheduled end", sizeFactor: 1 },
]

const tableService = new TableService()

type GroupMemberDetailsProps = {
  ppeSelectionModeEnabled: boolean
  memberProfiles: any[]
  selectedGroup: any
  selectedSubGroup: string
  selectedGroupCategory: string
  groupMemberPpeSelection: GroupMemberPpeProps | {}
  subGroupSelected: boolean
  groupCompleted: boolean
  loadingModalOpen: boolean
  allPpeInstances?: PpeInstanceRecordSchema[]
  ppeSelections?: PpeForProfile
  setPpeSelections?: (selections: PpeForProfile) => void
  getNonExpiredGroupsBelongingTo: (idPassport: string) => string[]
  onRowSelect: (selectedRows: any) => void
  onRowClick: (selectedRowData: Record<string, any>) => void
}

export const GroupMemberDetails = forwardRef((props: GroupMemberDetailsProps, ref) => {
  const [tableHeight, setTableHeight] = useState<number>(500)

  const selectedAssociation = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.selectedAssociation,
  )
  const associationRepo = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.associationRepo,
  ) as AssociationSettingsRepository

  useEffect(() => {
    const refObj = (ref as RefObject<HTMLDivElement>)?.current
    if (refObj) {
      const tableTopY = refObj.getBoundingClientRect().top
      const browserWindowHeight = window.innerHeight
      setTableHeight(browserWindowHeight - tableTopY)
    }
  }, [ref])

  const {
    memberProfiles,
    selectedGroup,
    selectedSubGroup,
    selectedGroupCategory,
    groupMemberPpeSelection,
    subGroupSelected,
    groupCompleted,
    loadingModalOpen,
    ppeSelectionModeEnabled,
    ppeSelections,
    setPpeSelections,
    getNonExpiredGroupsBelongingTo,
    onRowSelect,
    onRowClick,
  } = props

  const getColumnConfig = () => {
    if (subGroupSelected) {
      return [...COLUMN_CONFIG, { key: "ppeStatus", label: "PPE Status", sizeFactor: 1 }]
    }

    const statusColumn = {
      key: "status",
      label: "Status",
      sizeFactor: 0.2,
      renderCell: (rowData: Record<string, any>) => {
        return <div style={{ width: 80, height: "100%", alignContent: "center" }}>{rowData.row.rightComponent}</div>
      },
    }
    const columnConfig = [...COLUMN_CONFIG, statusColumn]
    return columnConfig
  }

  const getCustomComponentConfig = () => {
    if (!subGroupSelected) {
      return {
        rightHeaderComponent: <div style={styles.rightHeaderComponent}>Status</div>,
        rightComponentWidth: 80,
      }
    }
    return {}
  }

  const getCustomRowStyle = (rowId: string) => {
    if (!subGroupSelected) {
      const memberJobType = GroupService.getMemberJobType(rowId, selectedGroup)
      if (memberJobType) {
        // @ts-ignore
        const docRequirementsNotSatisfied = GroupService.getDocRequirementsNotSatisfied(
          rowId,
          memberJobType,
          associationRepo,
          selectedAssociation,
          selectedGroup,
        )
        if (true) {
          return `${rowStyles.rowHighlight} ${rowStyles.warningRed}`
          // {
          //   backgroundColor: ColorPalette.WARNING_RED_TRANSPARENT,
          //   // @ts-ignore
          //   ':hover': {
          //     filter: 'brightness(0.9)',
          //   },
          // }
        }
        if (getNonExpiredGroupsBelongingTo(rowId).length > 0) {
          return `${rowStyles.rowHighlight} ${rowStyles.mediumGrey}`
          // {
          //   backgroundColor: ColorPalette.DARK_MEDIUM_GREY_TRANSPARENT,
          //   // @ts-ignore
          //   ':hover': {
          //     filter: 'brightness(0.9)',
          //   },
          // }
        }
      }
    }
    return ""
  }

  if (ppeSelectionModeEnabled) {
    return (
      <GroupMemberPpe
        {...groupMemberPpeSelection}
        allPpeInstances={props.allPpeInstances}
        existingSelections={ppeSelections}
        // @ts-expect-error
        onSelectionChange={setPpeSelections}
      />
    )
  }
  let detailView = null
  if (memberProfiles.length > 0) {
    let tableData = memberProfiles
    const groupKey = GroupService.getInitiationGroupKeyByDisplayName(selectedSubGroup, selectedGroup)
    if (groupKey !== "All" && subGroupSelected && selectedGroup?.subGroups) {
      tableData = memberProfiles.filter((profile) => {
        // @ts-ignore
        return selectedGroup?.subGroups[selectedGroupCategory][groupKey].memberPks.includes(profile.id)
      })
    }
    const columnConfig = getColumnConfig()
    const tableColumnDef = tableService.convertPfColumnConfigToGridColDef(columnConfig)
    detailView = (
      // <DataTable
      //   ref={ref as React.RefObject<DataTable>}
      //   tableData={tableData}
      //   columnConfig={getColumnConfig()}
      //   tableWidth={subGroupSelected ? 9 : 10}
      //   onRowSelect={onRowSelect}
      //   selectedRowItemId={''}
      //   selectionEnabled={!groupCompleted && !subGroupSelected}
      //   applyCustomRowStyle={getCustomRowStyle}
      //   customComponentConfig={getCustomComponentConfig()}
      //   rowClickAsSelect={!subGroupSelected}
      //   onRowClick={onRowClick}
      //   disabled={!subGroupSelected}
      //   key={`dataTable_${selectedAssociation}-${subGroupSelected}-${groupKey}`}
      // />
      <div ref={ref as RefObject<HTMLDivElement>} style={{ width: "100%" }}>
        <Table
          rows={tableData}
          columns={tableColumnDef}
          tableHeight={tableHeight}
          rowHeight={TABLE_ROW_HEIGHT}
          rowSelection={!groupCompleted && !subGroupSelected}
          onRowClick={onRowClick}
          onRowSelection={(selectedRowIds: string[]) => console.info("selected row ids", selectedRowIds)}
          getRowClassName={(params: any) => getCustomRowStyle(params.id)}
        />
      </div>
    )
  } else if (!loadingModalOpen) {
    detailView = <UserFeedback message="No group members added." />
  }
  return detailView
})

const styles = {
  rightHeaderComponent: {
    fontSize: "0.8rem",
    fontWeight: 500,
    textAlign: "right" as "right",
    color: ColorPalette.PRIMARY_LIGHT_TEXT,
    width: "100%",
    paddingRight: "calc(80px - (6ch / 0.8))",
  },
}
